import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { UntilDestroy } from '@ngneat/until-destroy';
import { BehaviorSubject, concatMap, finalize, from } from 'rxjs';
import { delay } from 'rxjs/operators';

import { PuSubscribable } from '@app/utils/pu-subscribe';
import { InspectionModel } from '@main-application/inspections/models/inspection.model';
import { RestInspectionsModel } from '@main-application/inspections/models/rest-inspections-model.interface';
import { InspectionService } from '@main-application/inspections/services/inspection.service';
import { RestUserModel } from '@shared/interfaces/user.interface';
import { PipesModule } from '@shared/pipes/pipes.module';
import { openExternalUrl } from '@shared/utils/dom/window.util';
import { AvatarModule } from '@ui-components/components/avatar/avatar.module';
import { RadioButtonDropdownModule } from '@ui-components/controls/radio-button-dropdown/radio-button-dropdown.module';

export interface InspectionCheckDuplicatesModal {
  inspections: RestInspectionsModel[];
  templateName: string;
  propertyName: string;
  unitName: string;
  users: RestUserModel[];
}

@UntilDestroy()
@Component({
  standalone: true,
  selector: 'app-inspection-check-duplicates-modal',
  templateUrl: './inspection-check-duplicates-modal.component.html',
  styleUrls: ['./inspection-check-duplicates-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RadioButtonDropdownModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    AvatarModule,
    PipesModule,
    MatTooltipModule,
  ],
})
export class InspectionCheckDuplicatesModalComponent extends PuSubscribable {
  public dialogRef: MatDialogRef<InspectionCheckDuplicatesModalComponent> = inject(MatDialogRef);
  public data: InspectionCheckDuplicatesModal = inject(MAT_DIALOG_DATA);
  private router: Router = inject(Router);
  public inspectionService: InspectionService = inject(InspectionService);

  deletingInProgress$ = new BehaviorSubject<boolean>(false);
  userMap: Map<number, RestUserModel> = new Map();

  constructor() {
    super();

    this.data.users.forEach(user => {
      this.userMap.set(user.id, user);
    });
  }

  onKeepEditing(): void {
    this.dialogRef.close({ keepEditing: true });
  }

  onCancelAndCreate(): void {
    this.deletingInProgress$.next(true);
    const inspectionsToDelete = this.data.inspections.map(el => new InspectionModel(el, null));

    from(inspectionsToDelete)
      .pipe(
        concatMap((inspection: InspectionModel) => this.delete(inspection, true).pipe(delay(300))),
        finalize(() => {
          this.deletingInProgress$.next(false);
          this.dialogRef.close({ keepEditing: false });
        })
      )
      .subscribe()
      .untilDestroyed(this);
  }

  delete(inspection: InspectionModel, noConfirm = false) {
    return this.inspectionService.deleteInspection(inspection, noConfirm);
  }

  onNavigateToInspection(id: number): void {
    const url = this.router.createUrlTree(['/inspections/custom', id]).toString();
    openExternalUrl(url);
  }
}
